import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { PaymentService, SubscriptionPlan } from '../services/paymentService';
import { AuthManager, User } from '../utils/authManager';
import { UserTrialManager, UserTrialData } from '../utils/userTrialManager';
import UserBottomMenu from '../components/UserBottomMenu';
import PaymentMethodBrandIcon from '../components/PaymentMethodBrandIcon';

interface UserSubscriptionScreenProps {
  onBack: () => void;
  onSubscriptionSuccess?: (subscriptionId: string) => void;
  onTabPress?: (tab: 'dashboard' | 'appointments' | 'subscription' | 'profile') => void;
  onLogout?: () => void;
  onNavigateToChat?: () => void;
}

interface UserSubscription {
  id: string;
  planId: string;
  status: 'active' | 'cancelled' | 'past_due' | 'trialing';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

const UserSubscriptionScreen: React.FC<UserSubscriptionScreenProps> = ({
  onBack,
  onSubscriptionSuccess,
  onTabPress,
  onLogout,
  onNavigateToChat,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscription | null>(null);
  const [currentTrial, setCurrentTrial] = useState<UserTrialData | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  // Enhanced subscription plans for users
  const subscriptionPlans: SubscriptionPlan[] = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      currency: 'usd',
      interval: 'month',
      features: [
        'Up to 5 appointments/month',
        'Basic booking',
        'Email notifications',
        'Basic support',
        'Mobile app access',
      ],
      stripePriceId: 'price_free',
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 9.99,
      currency: 'usd',
      interval: 'month',
      features: [
        'Up to 50 appointments/month',
        'Advanced booking features',
        'SMS & Email notifications',
        'Priority support',
        'Calendar sync',
        'Appointment reminders',
        'Basic analytics',
      ],
      stripePriceId: 'price_premium_monthly',
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 19.99,
      currency: 'usd',
      interval: 'month',
      features: [
        'Up to 100 appointments/month',
        'All Premium features',
        'Advanced analytics',
        'Custom booking forms',
        'API access',
        '24/7 support',
        'Multi-service booking',
        'Payment processing',
      ],
      stripePriceId: 'price_pro_monthly',
    },
    {
      id: 'business',
      name: 'Business',
      price: 39.99,
      currency: 'usd',
      interval: 'month',
      features: [
        'Unlimited appointments',
        'All Pro features',
        'Team management',
        'Advanced reporting',
        'White-label options',
        'Custom integrations',
        'Dedicated support',
        'Priority feature requests',
      ],
      stripePriceId: 'price_business_monthly',
    },
  ];

  useEffect(() => {
    loadUserData();
    loadCurrentSubscription();
    loadCurrentTrial();
  }, []);

  const loadUserData = async () => {
    try {
      const userData = await AuthManager.getSavedUser();
      setUser(userData);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadCurrentSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      // In a real app, this would fetch from your backend
      // For demo purposes, we'll simulate no active subscription
      setCurrentSubscription(null);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const loadCurrentTrial = async () => {
    try {
      const trialData = await UserTrialManager.getTrialData();
      setCurrentTrial(trialData);
    } catch (error) {
      console.error('Error loading trial data:', error);
    }
  };

  const handleSelectPlan = (planId: string) => {
    setSelectedPlan(planId);
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) {
      Alert.alert('Error', 'Please select a plan');
      return;
    }

    if (!user) {
      Alert.alert('Error', 'User not found');
      return;
    }

    const plan = subscriptionPlans.find(p => p.id === selectedPlan);
    if (!plan) {
      Alert.alert('Error', 'Invalid plan selected');
      return;
    }

    // Free plan - no payment required
    if (plan.id === 'free') {
      Alert.alert(
        'Free Plan Activated',
        'You are now on the free plan. You can upgrade anytime.',
        [{ text: 'OK', onPress: () => onBack() }]
      );
      return;
    }

    // Trial plan - start 30-day trial
    if (plan.id === 'trial') {
      setLoading(true);
      try {
        const trialData = await UserTrialManager.startTrial('premium');
        setCurrentTrial(trialData);
        setLoading(false);
        
        Alert.alert(
          '🎉 Trial Started!',
          `Your 30-day free trial has begun! You now have access to all Premium features for ${trialData.daysLeft} days.`,
          [
            {
              text: 'Continue',
              onPress: () => {
                onSubscriptionSuccess?.('trial_' + Date.now());
                onBack();
              }
            }
          ]
        );
        return;
      } catch (error) {
        setLoading(false);
        Alert.alert('Error', 'Failed to start trial. Please try again.');
        return;
      }
    }

    setLoading(true);

    try {
      // Create payment intent
      const paymentResult = await PaymentService.createPaymentIntent(
        selectedPlan,
        user.id,
        undefined, // No payment method yet
        undefined // No wallet type yet
      );

      if (paymentResult.success) {
        // In a real app, you would redirect to Stripe Checkout or use Stripe Elements
        // For demo purposes, we'll simulate a successful subscription
        setTimeout(() => {
          setLoading(false);
          Alert.alert(
            'Subscription Successful!',
            `Your ${plan.name} subscription has been activated. You now have access to all premium features.`,
            [
              {
                text: 'Continue',
                onPress: () => {
                  onSubscriptionSuccess?.(paymentResult.subscriptionId || 'sub_' + Date.now());
                  onBack();
                }
              }
            ]
          );
        }, 2000);
      } else {
        setLoading(false);
        Alert.alert('Subscription Failed', paymentResult.error || 'Unknown error occurred');
      }
    } catch (error) {
      setLoading(false);
      Alert.alert('Error', 'An error occurred while processing your subscription');
    }
  };

  const handleWalletPayment = async (walletType: 'apple_pay' | 'google_pay') => {
    if (!selectedPlan || !user) {
      Alert.alert('Error', 'Please select a plan');
      return;
    }

    setLoading(true);

    try {
      const paymentResult = await PaymentService.createPaymentIntent(
        selectedPlan,
        user.id,
        undefined,
        walletType
      );

      if (paymentResult.success) {
        // Simulate wallet payment success
        setTimeout(() => {
          setLoading(false);
          Alert.alert(
            'Payment Successful!',
            `Your subscription has been activated using ${walletType === 'apple_pay' ? 'Apple Pay' : 'Google Pay'}.`,
            [
              {
                text: 'Continue',
                onPress: () => {
                  onSubscriptionSuccess?.(paymentResult.subscriptionId || 'sub_' + Date.now());
                  onBack();
                }
              }
            ]
          );
        }, 2000);
      } else {
        setLoading(false);
        Alert.alert('Payment Failed', paymentResult.error || 'Unknown error occurred');
      }
    } catch (error) {
      setLoading(false);
      Alert.alert('Payment Error', 'An error occurred while processing your payment');
    }
  };

  const getPlanBadge = (planId: string) => {
    switch (planId) {
      case 'free':
        return { text: 'Current', color: '#34C759' };
      case 'trial':
        return { text: 'Try Free', color: '#FF6B35' };
      case 'premium':
        return { text: 'Popular', color: '#FF9500' };
      case 'pro':
        return { text: 'Best Value', color: '#007AFF' };
      case 'business':
        return { text: 'Enterprise', color: '#8E8E93' };
      default:
        return null;
    }
  };

  const handleTabPress = (tab: 'dashboard' | 'appointments' | 'subscription' | 'profile') => {
    if (tab === 'dashboard' && onNavigateToChat) {
      // Home button goes to chat screen
      onNavigateToChat();
    } else if (onTabPress) {
      onTabPress(tab);
    }
  };

  if (subscriptionLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>Loading subscription options...</Text>
      </View>
    );
  }

  return (
    <View style={styles.mainContainer}>
      <ScrollView style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Choose Your Plan</Text>
        </View>

      <View style={styles.content}>
        <Text style={styles.welcomeText}>
          Welcome, {user?.name || 'User'}! Choose the plan that works best for you.
        </Text>

        {/* Current Subscription Status */}
        {currentSubscription && (
          <View style={styles.currentSubscriptionCard}>
            <Text style={styles.currentSubscriptionTitle}>Current Subscription</Text>
            <Text style={styles.currentSubscriptionText}>
              {subscriptionPlans.find(p => p.id === currentSubscription.planId)?.name} Plan
            </Text>
            <Text style={styles.currentSubscriptionStatus}>
              Status: {currentSubscription.status}
            </Text>
          </View>
        )}

        {/* Current Trial Status */}
        {currentTrial && currentTrial.isActive && !currentTrial.isExpired && (
          <View style={styles.trialStatusCard}>
            <Text style={styles.trialStatusTitle}>🎉 Free Trial Active</Text>
            <Text style={styles.trialStatusText}>
              {currentTrial.daysLeft} days remaining
            </Text>
            <Text style={styles.trialStatusDescription}>
              Enjoying all Premium features! Your trial will auto-convert to Premium plan.
            </Text>
          </View>
        )}

        {/* Expired Trial Status */}
        {currentTrial && currentTrial.isExpired && (
          <View style={styles.expiredTrialCard}>
            <Text style={styles.expiredTrialTitle}>⏰ Trial Expired</Text>
            <Text style={styles.expiredTrialText}>
              Your free trial has ended. Choose a plan to continue.
            </Text>
          </View>
        )}

        {/* Subscription Plans */}
        <View style={styles.plansContainer}>
          {subscriptionPlans.map((plan) => {
            const badge = getPlanBadge(plan.id);
            const isSelected = selectedPlan === plan.id;
            const isCurrentPlan = currentSubscription?.planId === plan.id;

            return (
              <TouchableOpacity
                key={plan.id}
                style={[
                  styles.planCard,
                  isSelected && styles.selectedPlan,
                  isCurrentPlan && styles.currentPlan,
                ]}
                onPress={() => !isCurrentPlan && handleSelectPlan(plan.id)}
                disabled={isCurrentPlan}
              >
                {badge && (
                  <View style={[styles.badge, { backgroundColor: badge.color }]}>
                    <Text style={styles.badgeText}>{badge.text}</Text>
                  </View>
                )}

                <View style={styles.planHeader}>
                  <Text style={styles.planName}>{plan.name}</Text>
                  <View style={styles.priceContainer}>
                    <Text style={styles.price}>
                      ${plan.price}
                    </Text>
                    <Text style={styles.pricePeriod}>
                      {plan.price === 0 ? 'forever' : `/${plan.interval}`}
                    </Text>
                  </View>
                </View>

                <View style={styles.featuresContainer}>
                  {plan.features.map((feature, index) => (
                    <View key={index} style={styles.featureItem}>
                      <Text style={styles.featureIcon}>✓</Text>
                      <Text style={styles.featureText}>{feature}</Text>
                    </View>
                  ))}
                </View>

                {isCurrentPlan && (
                  <View style={styles.currentPlanIndicator}>
                    <Text style={styles.currentPlanText}>Current Plan</Text>
                  </View>
                )}

                {isSelected && !isCurrentPlan && (
                  <View style={styles.selectedIndicator}>
                    <Text style={styles.selectedText}>Selected</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Payment Options */}
        {selectedPlan && subscriptionPlans.find(p => p.id === selectedPlan)?.price > 0 && (
          <View style={styles.paymentOptionsContainer}>
            <Text style={styles.paymentOptionsTitle}>Payment Options</Text>
            
            {/* Apple Pay / Google Pay */}
            <View style={styles.walletButtonsContainer}>
              {Platform.OS === 'ios' && (
                <TouchableOpacity 
                  style={styles.applePayButton}
                  onPress={() => handleWalletPayment('apple_pay')}
                  disabled={loading}
                >
                  <View style={styles.walletButtonRow}>
                    <PaymentMethodBrandIcon methodId="apple_pay" size={26} display="symbol" />
                    <Text style={styles.applePayText}>Pay with Apple Pay</Text>
                  </View>
                </TouchableOpacity>
              )}
              
              {Platform.OS === 'android' && (
                <TouchableOpacity 
                  style={styles.googlePayButton}
                  onPress={() => handleWalletPayment('google_pay')}
                  disabled={loading}
                >
                  <View style={styles.walletButtonRow}>
                    <PaymentMethodBrandIcon methodId="google_pay" size={26} display="symbol" />
                    <Text style={styles.googlePayText}>Pay with Google Pay</Text>
                  </View>
                </TouchableOpacity>
              )}
            </View>

            <View style={styles.divider}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>or</Text>
              <View style={styles.dividerLine} />
            </View>

            {/* Credit Card Payment */}
            <TouchableOpacity
              style={[styles.subscribeButton, loading && styles.subscribeButtonDisabled]}
              onPress={handleSubscribe}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <Text style={styles.subscribeButtonText}>
                  Subscribe with Credit Card
                </Text>
              )}
            </TouchableOpacity>
          </View>
        )}

        {/* Free Plan Selection */}
        {selectedPlan && subscriptionPlans.find(p => p.id === selectedPlan)?.price === 0 && (
          <TouchableOpacity
            style={[styles.subscribeButton, loading && styles.subscribeButtonDisabled]}
            onPress={handleSubscribe}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <Text style={styles.subscribeButtonText}>
                Activate Free Plan
              </Text>
            )}
          </TouchableOpacity>
        )}

        {/* No Plan Selected */}
        {!selectedPlan && (
          <View style={styles.noSelectionContainer}>
            <Text style={styles.noSelectionText}>
              Select a plan to see payment options
            </Text>
          </View>
        )}
      </View>
      </ScrollView>

      {/* Bottom Menu */}
      {onTabPress && (
        <UserBottomMenu
          activeTab="subscription"
          onTabPress={handleTabPress}
          onLogout={onLogout}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  header: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    paddingTop: 50,
    backgroundColor: '#007AFF',
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  backButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
  },
  content: {
    padding: 20,
  },
  welcomeText: {
    fontSize: 18,
    color: '#333',
    marginBottom: 20,
    textAlign: 'center',
  },
  currentSubscriptionCard: {
    backgroundColor: '#E8F5E8',
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#34C759',
  },
  currentSubscriptionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2E7D32',
    marginBottom: 4,
  },
  currentSubscriptionText: {
    fontSize: 14,
    color: '#2E7D32',
    marginBottom: 2,
  },
  currentSubscriptionStatus: {
    fontSize: 12,
    color: '#4CAF50',
  },
  trialStatusCard: {
    backgroundColor: '#E8F5E8',
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#34C759',
  },
  trialStatusTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2E7D32',
    marginBottom: 4,
  },
  trialStatusText: {
    fontSize: 14,
    color: '#2E7D32',
    marginBottom: 4,
    fontWeight: '600',
  },
  trialStatusDescription: {
    fontSize: 12,
    color: '#4CAF50',
  },
  expiredTrialCard: {
    backgroundColor: '#FFEBEE',
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#FFCDD2',
  },
  expiredTrialTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#C62828',
    marginBottom: 4,
  },
  expiredTrialText: {
    fontSize: 14,
    color: '#D32F2F',
  },
  plansContainer: {
    marginBottom: 30,
  },
  planCard: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 2,
    borderColor: '#E1E5E9',
    position: 'relative',
  },
  selectedPlan: {
    borderColor: '#007AFF',
    backgroundColor: '#F0F8FF',
  },
  currentPlan: {
    borderColor: '#34C759',
    backgroundColor: '#F0FFF0',
  },
  badge: {
    position: 'absolute',
    top: -10,
    right: 20,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    zIndex: 1,
  },
  badgeText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  planHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  planName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  price: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#007AFF',
  },
  pricePeriod: {
    fontSize: 14,
    color: '#8E8E93',
    marginLeft: 4,
  },
  featuresContainer: {
    marginBottom: 16,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  featureIcon: {
    fontSize: 16,
    color: '#34C759',
    marginRight: 12,
    fontWeight: 'bold',
  },
  featureText: {
    fontSize: 14,
    color: '#1C1C1E',
    flex: 1,
  },
  currentPlanIndicator: {
    position: 'absolute',
    top: 16,
    right: 16,
    backgroundColor: '#34C759',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  currentPlanText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  selectedIndicator: {
    position: 'absolute',
    top: 16,
    right: 16,
    backgroundColor: '#007AFF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  selectedText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  paymentOptionsContainer: {
    marginBottom: 20,
  },
  paymentOptionsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  walletButtonsContainer: {
    marginBottom: 16,
  },
  walletButtonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  applePayButton: {
    backgroundColor: '#000',
    paddingVertical: 16,
    borderRadius: 8,
    marginBottom: 10,
    alignItems: 'center',
  },
  applePayText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 10,
  },
  googlePayButton: {
    backgroundColor: '#4285F4',
    paddingVertical: 16,
    borderRadius: 8,
    marginBottom: 10,
    alignItems: 'center',
  },
  googlePayText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 10,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#e9ecef',
  },
  dividerText: {
    marginHorizontal: 16,
    fontSize: 14,
    color: '#666',
  },
  subscribeButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  subscribeButtonDisabled: {
    backgroundColor: '#B0B0B0',
  },
  subscribeButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  noSelectionContainer: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  noSelectionText: {
    fontSize: 16,
    color: '#666',
    fontStyle: 'italic',
  },
});

export default UserSubscriptionScreen;
